import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { User } from 'oidc-client-ts';
import { clearAuth, processAuthToken } from '@eo/shared';

import { useAppDispatch, useAppSelector } from '../app/store';
import { isPublicPath } from '../app/publicPaths';
import { AuthCallbackError } from '../components/AuthCallbackError';
import { userManager } from './userManager';

type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
};

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
});

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

type Props = { children: ReactNode };

// `isAuthenticated` derives from redux `state.app.userAuthenticated` so every
// consumer (StaffProvider, useAuth, the slice itself) reads the same flag.
export function AuthProvider({ children }: Props) {
  const dispatch = useAppDispatch();
  const userAuthenticated = useAppSelector((s) => s.app.userAuthenticated);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  // Tracks the last access_token we dispatched processAuthToken for. The OIDC
  // events.addUserLoaded listener and the warm-boot getUser() branch both
  // observe the same restored session — without this guard processAuthToken
  // would dispatch twice on warm boot and trigger duplicate boot fetches.
  const lastDispatchedTokenRef = useRef<string | null>(null);

  const syncOidcUser = useCallback(
    (loaded: User): boolean => {
      if (lastDispatchedTokenRef.current === loaded.access_token) return false;
      const rawEmail = loaded.profile?.email;
      const email = typeof rawEmail === 'string' ? rawEmail.trim() : '';
      if (!email) {
        // Misconfigured tenant — no `email` claim. Surface explicitly rather
        // than booting with email='' (staff resolution keys on it).
        console.error('[AuthProvider] IdP token missing email claim');
        setAuthError('Email claim missing from sign-in token.');
        return false;
      }
      lastDispatchedTokenRef.current = loaded.access_token;
      void dispatch(processAuthToken({ token: loaded.access_token, email }));
      return true;
    },
    [dispatch],
  );

  useEffect(() => {
    const onUserLoaded = (loaded: User) => {
      syncOidcUser(loaded);
    };
    const onUserUnloaded = () => {
      lastDispatchedTokenRef.current = null;
      // Mirror the OIDC unload into redux so consumers reading
      // `userAuthenticated` flip back to false in lockstep.
      dispatch(clearAuth());
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
    };
  }, [dispatch, syncOidcUser]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const existing = await userManager.getUser();
        if (cancelled) return;
        if (existing && !existing.expired) {
          syncOidcUser(existing);
          setIsLoading(false);
          return;
        }
        if (isPublicPath(window.location.pathname)) {
          setIsLoading(false);
          return;
        }
        await userManager.signinRedirect({
          state: { returnTo: window.location.pathname + window.location.search },
        });
      } catch (err) {
        if (cancelled) return;
        console.error('[AuthProvider] sign-in init failed:', err);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [syncOidcUser]);

  if (authError) {
    return (
      <AuthCallbackError
        onRetry={() => {
          setAuthError(null);
          lastDispatchedTokenRef.current = null;
          userManager
            .removeUser()
            .then(() => userManager.signinRedirect())
            .catch((retryErr) => {
              console.error('[AuthProvider] retry sign-in failed:', retryErr);
              setAuthError('Retry failed. Please reload and try again.');
            });
        }}
      />
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated: userAuthenticated, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}
